import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import utils from "../utils/utils";
import { apiRequest } from "../utils/request";
import { useUserStore } from "../modules/user/stores";

/** 与 utils/recallCapture.ts 共用同一个键 */
const CID_KEY = "recallCid";

/**
 * 老客召回：把匿名点击回填给登录后的用户。
 *
 * 后端 /rc-{code} 已完成 302 并落库点击（已登录时同时写入 userId），
 * 只有「匿名点击」才会在跳转地址上带 cid。转化统计全部锚定 userId，
 * 这一步决定归因能否成立。
 *
 * 职责只有「绑定」：cid 的抓取由 entry 的 captureRecallParams() 负责，
 * 本 hook 只从本地存储取。不要把抓取搬回这里 —— 落地目标可以配成 /recharge
 * 这类受保护路由，而召回面向的正是未登录老客，ProtectedRoute 会抢在本 effect
 * 之前 <Navigate replace> 把 rc/cid 从地址栏冲掉（详见 recallCapture.ts）。
 *
 * 登录判定必须用 store 的 isAuthenticated（基于已解析的 userInfo.userId），
 * 不能读 localStorage 里的 token：会话过期 / 被单点登录踢下线 / 改密码后，
 * token 作废但那串字符还在，判定会一直是「已登录」，于是每次路由变化都去打一次
 * bind、服务端每次都回未登录，cid 永远清不掉 —— 无限重试。
 * 同理也不能用 utils.getUser()，logout 存进去的是字符串 "null"（真值）。
 *
 * 绑定完成即清除本地 cid，之后不再发起任何请求。
 * 全程静默：任何失败都不影响页面，也不侵入登录流程本身。
 */
export function useRecallTracking(): void {
  const location = useLocation();
  const isAuthenticated = useUserStore(state => state.isAuthenticated());

  useEffect(() => {
    void (async () => {
      try {
        const cid = utils.getObject(CID_KEY);
        if (!cid || !isAuthenticated) return;

        // 读 extra 不是 model：后端 bind 返回的是 ApiResp.success(boolean)，
        // 而 ApiResp 把值放在 extra 字段上（model 是 ApiModelResp 才有的）。
        // 之前读 model 永远是 undefined，cid 从来没被清掉过 —— 后果是只要用户
        // 登录着，站内每次路由变化都会再打一次 bind，停不下来。
        const resp = (await apiRequest(
          "/api/recall/bind",
          { cid },
          { alert: false }
        )) as {
          data?: { extra?: boolean };
        };
        if (resp?.data?.extra === true) {
          utils.setObject(CID_KEY, null);
        }
      } catch {
        // 召回统计失败不影响任何业务流程
      }
    })();
    // isAuthenticated 入依赖：用户在当前页登录完成时立刻补绑，不必等下一次路由跳转
  }, [location, isAuthenticated]);
}
